import React, { useState } from 'react';
import { MessageCircle, X, Send, Lock, CheckCircle } from 'lucide-react';
import './ChatPopup.css';
import { useFormsSwitchedOff, PreviewFormNotice } from './PreviewFormNotice';

const ChatPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const formsOff = useFormsSwitchedOff();
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    message: '',
    bestTimeToContact: '',
    contactPreference: 'email'
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formsOff) return;
    setSubmitting(true);
    setSubmitError('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Create FormData object (backend expects form data, not JSON)
      const formDataToSend = new FormData();
      formDataToSend.append('first_name', formData.firstName);
      formDataToSend.append('last_name', formData.lastName);
      formDataToSend.append('phone_number', formData.phoneNumber);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('best_time_to_contact', formData.bestTimeToContact);
      formDataToSend.append('contact_preference', formData.contactPreference);

      const response = await fetch(`${backendUrl}/api/contact/submit`, {
        method: 'POST',
        body: formDataToSend
        // Note: Don't set Content-Type header - browser will set it automatically with boundary
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitSuccess(true);
        // Reset form after 3 seconds and close popup
        setTimeout(() => {
          setSubmitSuccess(false);
          setIsOpen(false);
          setFormData({
            firstName: '',
            lastName: '',
            phoneNumber: '',
            email: '',
            message: '',
            bestTimeToContact: '',
            contactPreference: 'email'
          });
        }, 3000);
      } else {
        // Handle error message - could be string, array, or object
        let errorMsg = 'Submission failed. Please try again.';
        if (typeof data.detail === 'string') {
          errorMsg = data.detail;
        } else if (Array.isArray(data.detail)) {
          errorMsg = data.detail.map(err => err.msg || err).join(', ');
        } else if (data.message) {
          errorMsg = data.message;
        }
        setSubmitError(errorMsg);
      }
    } catch (error) {
      setSubmitError('Network error. Please check your connection and try again.');
      console.error('Contact form error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const togglePopup = () => {
    setIsOpen(!isOpen);
    // Reset success state when reopening
    if (!isOpen) {
      setSubmitSuccess(false);
      setSubmitError('');
    }
  };

  return (
    <>
      {/* Floating Chat Button */}
      <button 
        className="chat-popup-button"
        onClick={togglePopup}
        aria-label="Open contact form"
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {/* Popup Modal */}
      {isOpen && (
        <div className="chat-popup-modal">
          <div className="chat-popup-header">
            <Send size={20} />
            <h3>Send us a Message</h3>
            <button 
              className="chat-popup-close"
              onClick={togglePopup}
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          <div className="chat-popup-body">
            {submitSuccess ? (
              <div className="chat-success-message">
                <CheckCircle size={48} className="success-icon" />
                <h4>Message Sent!</h4>
                <p>We'll get back to you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="chat-popup-form">
                {formsOff && <PreviewFormNotice />}
                <div className="privacy-notice-top">
                  <p className="body-small" style={{ fontStyle: 'italic', color: 'var(--text-secondary)', marginBottom: '1rem', fontSize: '0.85rem' }}>
                    We take your privacy seriously. All data and files you submit are securely stored in Australia in compliance with The Privacy Act (1988) and the Australian Privacy Principles (APPs). You can find full details in our <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>Privacy Policy</a>.
                  </p>
                </div>

                <div className="form-group">
                  <label htmlFor="chat-firstName">First Name *</label>
                  <input
                    type="text"
                    id="chat-firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    disabled={submitting}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="chat-lastName">Last Name *</label>
                  <input
                    type="text"
                    id="chat-lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="chat-email">Email Address *</label>
                  <input
                    type="email"
                    id="chat-email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="chat-phoneNumber">Phone Number</label>
                  <input
                    type="tel"
                    id="chat-phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="chat-contactPreference">Preferred Contact Method *</label>
                  <select
                    id="chat-contactPreference"
                    name="contactPreference"
                    value={formData.contactPreference}
                    onChange={handleInputChange}
                    required
                    disabled={submitting}
                  >
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="sms">SMS</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="chat-bestTimeToContact">Best Time to Contact</label>
                  <input
                    type="text"
                    id="chat-bestTimeToContact"
                    name="bestTimeToContact"
                    value={formData.bestTimeToContact}
                    onChange={handleInputChange}
                    placeholder="e.g., Weekday mornings"
                    disabled={submitting}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="chat-message">Message *</label>
                  <textarea
                    id="chat-message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="3"
                    required
                    disabled={submitting}
                    placeholder="How can we help you?"
                  ></textarea>
                </div>

                {submitError && (
                  <div className="error-message">
                    <p>{submitError}</p>
                  </div>
                )}

                <div className="chat-form-footer">
                  <p className="body-small consent-text" style={{ marginBottom: '0.75rem', fontSize: '0.85rem' }}>
                    By submitting this form, you confirm that you have read and agree to our <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">privacy policy</a>.
                  </p>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={submitting || formsOff}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner"></span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={20} />
                        Send Message
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ChatPopup;
